import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Switch, Route, withRouter } from 'react-router-dom';
import { NoTemplateRoute, UnAuthedRoute, AuthedRoute, RedirectRoute, SupportRoute } from './route-handlers';
import routePaths from './route-paths';
import routeGuards from './route-guards';
import NotFoundLayout from './layouts/not-found-layout';
import { SignIn, SSOSignIn, SSOTokenExchange } from './pages/login';
import ForgotPassword from './pages/login/forgot-password';
import RegisterInvitee from './pages/login/register-invitee';
import Logout from './pages/logout';
import ChangePassword from './pages/login/change-password';
import UserManagement from './pages/users/user-management';
import ItemCatalog from './pages/items/catalog';
import ItemAbout from './pages/items/item-about';
import ItemHistory from './pages/items/item-history';
import SiteSettings from './pages/site-settings/site-settings';
import Staging from './pages/staging/staging';
import { Inventory } from './pages/inventory';
import { OfferReview, OfferLogisticsReview } from './pages/offers';
import SetResetPassword from './pages/login/set-reset-password';
import BulkItemImport from './pages/bulk-item-import/bulk-item-import';
import SiteList from './pages/admin/site-list';
import AddSite from './pages/admin/add-site';
import ProxyView from './pages/proxy';
import TransactionHistory from './pages/transaction-history/transaction-history'; // Why is the named export used instead of the default?
import Transactions from './pages/transactions/transactions';
import { Negotiations, NegotiationsDetail, SuggestedNegotiations } from './pages/negotiations';
import { CatalogDetail } from './pages/staging';
import { CustomerProfileDetail, CustomerProfiles } from './pages/customers';
import OrgSettings from './pages/org-settings/org-settings';
import featureFlags from './enums/feature-flags';
import FeatureFlagSwitcher from './feature-flag-switcher';
import HolisticsDashboard from './pages/holistics-dashboard';
import { DASHBOARD_LOCATIONS } from './enums/holistics-dashboard-enums';
import OfferReviewV2 from './pages/offer-review/offer-reviewV2';
import BatchComponentLoader from './utilities/batch-component-loader';
import DemoDashboard from './pages/demo-dashboard';
import ConfirmInvite from './pages/login/confirm-invite';
import { LandingPage } from './pages/landing-page/landing-page';
import Tracking from './utilities/tracking-apis';
import checkFeature from './helpers/check-feature-flag';
import OfferManagementRoot from './pages/offer-management-root';

const legacyPageLoader = new BatchComponentLoader(() => import('./pages/legacy-pages'));

const TaxAbout = legacyPageLoader.lazyLoad('TaxAbout');
const TaxDashboard = legacyPageLoader.lazyLoad('TaxDashboard');
const TaxRecipt = legacyPageLoader.lazyLoad('TaxRecipt');
const ActivityClaims = legacyPageLoader.lazyLoad('ActivityClaims');
const ActivityPosts = legacyPageLoader.lazyLoad('ActivityPosts');
const PendingVerifications = legacyPageLoader.lazyLoad('PendingVerifications');
const LogTransaction = legacyPageLoader.lazyLoad('LogTransaction');
const Post = legacyPageLoader.lazyLoad('Post');
const BulkActivityImport = legacyPageLoader.lazyLoad('BulkActivityImport');
const EditTransaction = legacyPageLoader.lazyLoad('EditTransaction');
const EnterpriseDashboard = legacyPageLoader.lazyLoad('EnterpriseDashboard');

class RouteSwitch extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    user: PropTypes.object,
  };

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.onRouteChanged(this.props.location);
    }
  }

  onRouteChanged(location) {
    Tracking.PageChange(location);
  }

  basicFeatureFlagCheck(featureFlag) {
    return checkFeature(featureFlag);
  }

  offerReviewFeatureFlagCheck() {
    const isOfferShiftEnabled = checkFeature(featureFlags.offerShift);
    const isOfferShiftRecalculationEnabled = checkFeature(featureFlags.offerShiftRecalculation);
    return isOfferShiftEnabled && !isOfferShiftRecalculationEnabled;
  }

  render() {
    const { user } = this.props;
    return (
      <Switch>
        <UnAuthedRoute exact path={routePaths.signIn} component={SignIn} />
        <UnAuthedRoute exact path={`${routePaths.ssoSignIn}`} component={SSOSignIn} />
        <UnAuthedRoute exact path={`${routePaths.ssoSignIn}/:path`} component={SSOSignIn} />
        <NoTemplateRoute exact path={`${routePaths.ssoTokenExchange}`} component={SSOTokenExchange} />
        <UnAuthedRoute exact path={routePaths.forgotPassword} component={ForgotPassword} />
        <UnAuthedRoute exact path={`${routePaths.resetPassword}:token`} component={SetResetPassword} />
        <UnAuthedRoute exact path={`${routePaths.confirmInvite}:token`} component={ConfirmInvite} />
        <UnAuthedRoute exact path={`${routePaths.registerInvitee}:token`} component={RegisterInvitee} />
        <SupportRoute exact path={routePaths.supportSSO} user={user} />

        <AuthedRoute
          path={routePaths.platformDashboard}
          locationDefault={DASHBOARD_LOCATIONS.DASHBOARD_TAB}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.basicFeatureFlagCheck(featureFlags.demoStaticReport)}
          isOn={DemoDashboard}
          isOff={HolisticsDashboard}
          user={user}
          basePath={`${routePaths.platformDashboard}/`}
          guard={routeGuards.holisticsDashboard}
        />
        <AuthedRoute
          exact
          path={routePaths.home}
          locationDefault={DASHBOARD_LOCATIONS.DASHBOARD_TAB}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.basicFeatureFlagCheck(featureFlags.demoStaticReport)}
          isOn={DemoDashboard}
          isOff={HolisticsDashboard}
          user={user}
          basePath={`${routePaths.home}`}
          guard={routeGuards.holisticsDashboard}
        />
        <AuthedRoute
          path={`${routePaths.home}r/`}
          locationDefault={DASHBOARD_LOCATIONS.DASHBOARD_TAB}
          user={user}
          basePath={routePaths.home}
          guard={routeGuards.holisticsDashboard}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.basicFeatureFlagCheck(featureFlags.demoStaticReport)}
          isOn={DemoDashboard}
          isOff={HolisticsDashboard}
        />
        <AuthedRoute
          path={routePaths.reportsDashboard}
          locationDefault={DASHBOARD_LOCATIONS.REPORTS_TAB}
          basePath={`${routePaths.reportsDashboard}/`}
          guard={routeGuards.holisticsDashboard}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.basicFeatureFlagCheck(featureFlags.demoStaticReport)}
          isOn={DemoDashboard}
          isOff={HolisticsDashboard}
          user={user}
        />
        <RedirectRoute exact path={routePaths.dashboard} redirectPath={routePaths.home} />
        <RedirectRoute exact path={routePaths.marketplace} redirectPath={routePaths.home} />

        <AuthedRoute exact path={routePaths.changePassword} component={ChangePassword} user={user} />

        <AuthedRoute path={routePaths.landingPage} component={LandingPage} user={user} showCycleStartDate={true} hideProgressBars={true} />
        <AuthedRoute
          exact
          path={routePaths.enterpriseDashboard}
          component={EnterpriseDashboard}
          user={user}
          guard={routeGuards.enterpriseDashboard}
        />
        <AuthedRoute exact path={routePaths.taxDashboard} component={TaxDashboard} user={user} guard={routeGuards.tax} />
        <AuthedRoute exact path={routePaths.taxAbout} component={TaxAbout} user={user} guard={routeGuards.tax} />
        <AuthedRoute exact path={`${routePaths.taxReceipt}:id`} component={TaxRecipt} user={user} guard={routeGuards.tax} />
        <AuthedRoute exact path={routePaths.logTransaction} component={LogTransaction} user={user} />
        <AuthedRoute exact path={`${routePaths.editTransaction}:id`} component={EditTransaction} user={user} />
        <AuthedRoute exact path={`${routePaths.post}:id`} component={Post} user={user} guard={routeGuards.post} />
        <AuthedRoute exact path={routePaths.myPosts} component={ActivityPosts} user={user} guard={routeGuards.posts} />
        <AuthedRoute exact path={routePaths.myClaims} component={ActivityClaims} user={user} guard={routeGuards.claims} />
        <AuthedRoute exact path={routePaths.myVerifications} component={PendingVerifications} user={user} guard={routeGuards.claims} />
        <RedirectRoute exact path={routePaths.pendingVerifications} redirectPath={routePaths.myVerifications} />

        <AuthedRoute exact path={routePaths.userManagement} component={UserManagement} user={user} guard={routeGuards.userManagement} />
        <AuthedRoute exact path={`${routePaths.proxy}:type/:id/:filename`} component={ProxyView} user={user} />
        <AuthedRoute exact path={routePaths.items} component={ItemCatalog} user={user} guard={routeGuards.items} />
        <AuthedRoute exact path={routePaths.itemAbout} component={ItemAbout} user={user} />
        <AuthedRoute exact path={routePaths.itemHistory} component={ItemHistory} user={user} guard={routeGuards.items} />
        <AuthedRoute exact path={`${routePaths.itemHistoryDetail}:id/:itemName`} component={ItemHistory} user={user} />
        <AuthedRoute exact path={routePaths.itemImport} component={BulkItemImport} user={user} />
        <AuthedRoute exact path={routePaths.siteSettings} component={SiteSettings} user={user} hideProgressBars={true} />
        <AuthedRoute path={routePaths.orgSettings} component={OrgSettings} user={user} hideProgressBars={true} />
        <AuthedRoute exact path={routePaths.inventory} component={Inventory} user={user} guard={routeGuards.inventory} />
        <AuthedRoute exact path={routePaths.staging} component={Staging} user={user} guard={routeGuards.inventory} />
        <AuthedRoute exact path={`${routePaths.staging}/:catalogId`} component={CatalogDetail} user={user} guard={routeGuards.inventory} />
        <AuthedRoute
          exact
          path={routePaths.review}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.offerReviewFeatureFlagCheck()}
          isOn={OfferReviewV2}
          isOff={OfferReview}
          user={user}
          guard={routeGuards.receiveOffer}
        />
        <AuthedRoute
          exact
          path={`${routePaths.offerReview}/:buyerSiteId/:buyerSiteName`}
          component={OfferLogisticsReview}
          user={user}
          guard={routeGuards.receiveOffer}
        />
        <AuthedRoute path={routePaths.offerComparison} component={OfferManagementRoot} user={user} guard={routeGuards.receiveOffer} />
        <AuthedRoute exact path={routePaths.negotiations} component={Negotiations} user={user} guard={routeGuards.receiveOffer} />
        <AuthedRoute
          exact
          path={`${routePaths.negotiations}/:buyerSiteId/:buyerSiteName`}
          component={NegotiationsDetail}
          user={user}
          guard={routeGuards.receiveOffer}
        />
        <AuthedRoute
          exact
          path={routePaths.suggestedNegotiations}
          component={FeatureFlagSwitcher}
          featureFlagCheck={() => this.basicFeatureFlagCheck(featureFlags.suggestedNegotiations)}
          isOn={SuggestedNegotiations}
          isOff={Negotiations}
          user={user}
          guard={routeGuards.receiveOffer}
        />
        <AuthedRoute exact path={routePaths.donationImport} component={BulkActivityImport} user={user} guard={routeGuards.import} />
        <AuthedRoute exact path={routePaths.impersonate} component={SiteList} user={user} />
        <AuthedRoute exact path={routePaths.addSite} component={AddSite} user={user} />
        <AuthedRoute exact path={routePaths.logout} component={Logout} user={user} />
        <AuthedRoute exact path={routePaths.history} component={TransactionHistory} user={user} guard={routeGuards.inventory} />
        <AuthedRoute exact path={routePaths.transactions} component={Transactions} user={user} guard={routeGuards.transactions} />
        <AuthedRoute exact path={routePaths.customers} component={CustomerProfiles} user={user} />
        <AuthedRoute exact path={`${routePaths.customers}/:buyerSiteName`} component={CustomerProfileDetail} user={user} />
        <Route component={NotFoundLayout} />
      </Switch>
    );
  }
}

const RoutedSwitch = withRouter(RouteSwitch);

export default class Routes extends Component {
  render() {
    return (
      <Router>
        <RoutedSwitch user={this.props.user} />
      </Router>
    );
  }
}

Routes.propTypes = {
  user: PropTypes.object,
};
